import React, { FC } from "react";
import { useIntl } from "react-intl";
import Paper from "../../Unknown/Paper";
import Button from "../../Unknown/Button";
import Box from "../../Unknown/Box";
import Typography from "../../Unknown/Typography";
import Alert from "../../Unknown/Alert";
import AlertTitle from "../../Unknown/AlertTitle";
import Checkbox from "../../Unknown/Checkbox";
import FormControlLabel from "../../Unknown/FormControlLabel";
import CircularProgress from "../../Unknown/CircularProgress";
import PatientMergeColumn, {
  PATIENT_MERGE_GRID_ROW_COUNT,
} from "./PatientMergeColumn";
import AddPatientToMergeSearch from "./AddPatientToMergeSearch";
import { PatientMergeSearchResult } from "./useSearchPatientsForMerge";
import {
  PatientInMerge,
  PatientFieldSelections,
  PatientMergeError,
  PatientMergeFieldKey,
  PatientMergeResult,
} from "./types";
import messages from "./messages";

type MergePatientsPageContentProps = {
  patients: PatientInMerge[];
  fieldSelections: PatientFieldSelections;
  isWard: boolean;
  isFamilyMember: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  mergeResult: PatientMergeResult | null;
  errorMessage: PatientMergeError | null;
  blockerError: PatientMergeError | null;
  onFieldSelect: (field: PatientMergeFieldKey, patientId: string) => void;
  onAddPatient: (patient: PatientMergeSearchResult) => void;
  onRemovePatient: (patientId: string) => void;
  onToggleWard: (checked: boolean) => void;
  onToggleFamilyMember: (checked: boolean) => void;
  onSubmit: () => void;
  onDone: () => void;
  onViewPatient: (selectedPatientMrn: string) => void;
  onPatientClick: (mrn: string) => void;
};

const MergePatientsPageContent: FC<MergePatientsPageContentProps> = ({
  patients,
  fieldSelections,
  isWard,
  isFamilyMember,
  canSubmit,
  isSubmitting,
  mergeResult,
  errorMessage,
  blockerError,
  onFieldSelect,
  onAddPatient,
  onRemovePatient,
  onToggleWard,
  onToggleFamilyMember,
  onSubmit,
  onDone,
  onViewPatient,
  onPatientClick,
}) => {
  const { formatMessage } = useIntl();
  const alreadySelectedIds = patients.map((p) => p.id);
  const canAddMore = patients.length < 4;
  const showAddPlaceholder = canAddMore && patients.length < 2;

  // fieldSelections.customer holds the winning patient's id, not the
  // customer's own id — resolve through that patient's linkedCustomer.
  const selectedCustomerId = fieldSelections.customer;
  const selectedCustomerPatient = patients.find(
    (p) => p.id === selectedCustomerId,
  );
  const customerName = selectedCustomerPatient?.linkedCustomer?.name ?? "";
  const showRelationshipCheckboxes = Boolean(selectedCustomerId);

  if (mergeResult) {
    return (
      <Paper sx={{ p: "30px" }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {formatMessage(messages.mergedSuccess, {
              name: mergeResult.selectedPatientName,
            })}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => onViewPatient(mergeResult.selectedPatientMrn)}
            sx={{
              p: 0,
              minWidth: 0,
              color: "inherit",
              textTransform: "none",
              textDecoration: "underline",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {formatMessage(messages.viewPatient)}
          </Button>
        </Alert>
      </Paper>
    );
  }

  if (isSubmitting) {
    return (
      <Paper sx={{ p: "30px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 360,
            gap: 5,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="body1" textAlign="center">
            {formatMessage(messages.processing)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: "30px", display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ flex: "1 1 50%" }}>
          <Typography variant="h5">{formatMessage(messages.title)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.subtitle)}
          </Typography>
        </Box>
        {canAddMore && (
          <Box sx={{ flex: "1 1 50%" }}>
            <AddPatientToMergeSearch
              alreadySelectedIds={alreadySelectedIds}
              onSelect={onAddPatient}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            // CSS Grid so every column's rows share a row and sync height.
            display: "grid",
            gridAutoFlow: "column",
            gridTemplateColumns: `repeat(${
              patients.length + (showAddPlaceholder ? 1 : 0)
            }, minmax(400px, 1fr))`,
            gridTemplateRows: `repeat(${PATIENT_MERGE_GRID_ROW_COUNT}, auto)`,
            overflowX: "auto",
            overflowY: "hidden",
            // Divider = 1px gap + container background showing through
            // (cells paint their own white) — avoids stacked translucent
            // borders rendering darker than intended at column boundaries.
            columnGap: "1px",
            backgroundColor: "rgba(0, 0, 0, 0.23)",
          }}
        >
          {patients.map((patient) => (
            <PatientMergeColumn
              key={patient.id}
              patient={patient}
              fieldSelections={fieldSelections}
              onFieldSelect={onFieldSelect}
              showRemove={patients.length > 1}
              onRemove={onRemovePatient}
              onPatientClick={onPatientClick}
            />
          ))}

          {showAddPlaceholder && (
            <Box
              sx={{
                gridRow: `span ${PATIENT_MERGE_GRID_ROW_COUNT}`,
                p: 3,
                display: "flex",
                backgroundColor: "background.paper",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  border: "1px dashed",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  minHeight: 240,
                }}
              >
                <Typography variant="body1" textAlign="center">
                  {formatMessage(messages.addAnotherPatient)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {showRelationshipCheckboxes && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 3,
              px: "16px",
              py: "16px",
              borderTop: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.23)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isWard}
                  onChange={(e) => onToggleWard(e.target.checked)}
                />
              }
              label={formatMessage(messages.isWardOf, { customerName })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isFamilyMember}
                  onChange={(e) => onToggleFamilyMember(e.target.checked)}
                />
              }
              label={formatMessage(messages.isFamilyMemberOf, { customerName })}
            />
          </Box>
        )}
      </Box>

      {blockerError ? (
        <Alert severity="error">
          <AlertTitle>{blockerError.title}</AlertTitle>
          {blockerError.description}
        </Alert>
      ) : (
        errorMessage && (
          <Alert severity="error">
            <AlertTitle>{errorMessage.title}</AlertTitle>
            {errorMessage.description}
          </Alert>
        )
      )}

      {canSubmit && !errorMessage && !blockerError && (
        <Alert severity="warning">
          <AlertTitle>{formatMessage(messages.warningTitle)}</AlertTitle>
          {formatMessage(messages.warningDescription)}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button variant="text" onClick={onDone} disabled={isSubmitting}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {formatMessage(messages.merge)}
        </Button>
      </Box>
    </Paper>
  );
};

export default MergePatientsPageContent;
